import React from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartHandshake, Users, Home, ArrowRight, ShieldCheck, Globe, Award,
  UserCheck, Target, Eye, MapPin, HandHeart, FileCheck, Megaphone, UserPlus,
  GraduationCap, Stethoscope, Trees, Droplets, Trophy, Baby, Siren, Landmark,
  Building2, Sparkles, Check,
} from "lucide-react";

const IMG = {
  people: "https://images.unsplash.com/photo-1629360021730-3d258452c425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtdWx0aWdlbmVyYXRpb25hbCUyMGZhbWlseSUyMHZpZGVvJTIwY2FsbCUyMGxhcHRvcHxlbnwwfHx8fDE3ODg1NDg3OTh8MA&ixlib=rb-4.1.0&q=85",
  community: "https://images.unsplash.com/photo-1601595989649-18174ba1b060?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxwdW5qYWIlMjB2aWxsYWdlJTIwZ3VyZHdhcmElMjBnb2xkZW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzg4NTQ4Nzk4fDA&ixlib=rb-4.1.0&q=85",
  town: "https://images.unsplash.com/photo-1655176199121-361d990effd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwdW5qYWIlMjB2aWxsYWdlJTIwZ3VyZHdhcmElMjBnb2xkZW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzg4NTQ4Nzk4fDA&ixlib=rb-4.1.0&q=85",
};

const PILLARS = [
  { icon: ShieldCheck, title: "Verified Communities", sub: "Trusted and authentic", color: "text-people" },
  { icon: Users, title: "Stronger Connections", sub: "Across generations", color: "text-community" },
  { icon: HandHeart, title: "Support Local Growth", sub: "People helping people", color: "text-terracotta" },
  { icon: Globe, title: "A Global Family", sub: "One town at a time", color: "text-people" },
];

const DIFFERENTIATORS = [
  { icon: Award, title: "Verified Town Administration", badge: "Platform Approved", text: "Every town hub is reviewed and approved by the Connect team, then led by verified local administrators. Official updates carry a gold verification badge — no anonymous pages." },
  { icon: UserCheck, title: "Verified Membership", badge: "Vetted Members", text: "Members are approved to join their town by its admin — tied to residency or diaspora roots. It keeps discourse warm, real and troll-free." },
  { icon: Target, title: "Community Projects", badge: "Real-World Impact", text: "Back genuine local initiatives — clean-water wells, scholarships, school improvements — each with a clear goal and visible progress." },
  { icon: Eye, title: "100% Transparency", badge: "Open Ledgers", text: "Live pledge counters and full supporter lists keep every project accountable. You always see who's helping and how far it's come." },
];

const STEPS = [
  {
    icon: FileCheck, step: "1", title: "Create an official town community",
    text: "An authorized representative of a town or village requests to establish an official page — with town info, administrator details, photos, and leadership approval.",
    tags: ["Town / village info", "Administrator info", "Photos", "Supporting documents", "Leadership approval"],
    note: "The Connect team reviews and approves each community before it goes live.",
  },
  {
    icon: Megaphone, step: "2", title: "Local administrators manage it",
    text: "Once approved, verified admins run one centralized channel for residents at home and abroad.",
    tags: ["Community news", "Announcements", "Development updates", "Events", "Public notices", "Volunteer opportunities", "Fundraising", "Infrastructure projects", "Emergency information", "Requests for assistance"],
    note: "One trusted place instead of scattered chats and unofficial groups.",
  },
  {
    icon: UserPlus, step: "3", title: "People join their hometown community",
    text: "Anyone connected to the town requests to join. A person from Begowal now living in Canada, the US, the UK, Australia or anywhere else can request membership.",
    tags: ["Residents", "Former residents", "Families abroad", "Supporters"],
    note: "The town administrator reviews and approves each request.",
  },
];

const PROJECTS = [
  { icon: GraduationCap, label: "Scholarships" },
  { icon: Building2, label: "School improvements" },
  { icon: Stethoscope, label: "Medical assistance" },
  { icon: Trees, label: "Parks & playgrounds" },
  { icon: Trophy, label: "Sports programs" },
  { icon: Droplets, label: "Clean-water projects" },
  { icon: Landmark, label: "Community centers" },
  { icon: Trees, label: "Tree planting" },
  { icon: MapPin, label: "Street improvements" },
  { icon: HandHeart, label: "Families in need" },
  { icon: Users, label: "Senior assistance" },
  { icon: Baby, label: "Youth programs" },
  { icon: Siren, label: "Emergency relief" },
  { icon: Sparkles, label: "Local entrepreneurship" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F7FF] via-[#F4F9F5] to-[#FAFAF7] font-body">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[76px] flex items-center">
          <div className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-11 h-11 rounded-xl bg-terracotta flex items-center justify-center shrink-0">
              <HeartHandshake className="text-white" size={26} strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <span className="font-serif font-extrabold text-2xl text-[#1A1A1A]">Connect</span>
              <span className="block text-[9px] sm:text-[10px] tracking-[0.22em] font-bold text-terracotta uppercase mt-1">People · Communities · Brighter Tomorrows</span>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-1 ml-10">
            <a href="#about" data-testid="nav-about" className="px-4 py-2 rounded-full font-semibold text-[#4A4A4A] hover:text-community hover:bg-community-light transition-colors">About</a>
            <a href="#how-it-works" data-testid="nav-how-it-works" className="px-4 py-2 rounded-full font-semibold text-[#4A4A4A] hover:text-community hover:bg-community-light transition-colors">How It Works</a>
            <a href="#for-town-admins" data-testid="nav-for-town-admins" className="px-4 py-2 rounded-full font-semibold text-[#4A4A4A] hover:text-community hover:bg-community-light transition-colors">For Town Admins</a>
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate("/login")} data-testid="landing-login-btn"
              className="min-h-[48px] px-5 rounded-full font-bold text-[#1A1A1A] hover:bg-black/5 transition-colors">Login</button>
            <button onClick={() => navigate("/register")} data-testid="landing-signup-btn"
              className="min-h-[48px] px-6 rounded-full font-bold bg-terracotta text-white hover:bg-terracotta-dark transition-transform active:scale-95">Sign Up</button>
          </div>
        </div>
      </header>

      {/* Hero two-path split */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-16">
        <span className="font-hand text-2xl text-people/80 -rotate-3 absolute left-2 top-44 hidden xl:block leading-tight">Different Places<br/>Same People<br/>Always Connected</span>
        <span className="font-hand text-2xl text-community/80 rotate-2 absolute right-2 top-44 hidden xl:block leading-tight text-right">Our Roots<br/>Our People<br/>A Brighter Tomorrow</span>

        <div className="text-center max-w-3xl mx-auto animate-rise">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.1]">
            Stay Connected to People.<br />Stay Connected to <span className="text-community">Your Roots.</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#4A4A4A] leading-relaxed mt-5 max-w-2xl mx-auto">
            No matter where life takes you, your connections and your roots remain important. Connect brings people together in two meaningful ways — choose how you want to connect.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-12">
          {/* People card */}
          <div data-testid="hero-card-connect-people" className="bg-white rounded-3xl border-2 border-people-border shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col">
            <div className="relative h-52 sm:h-60">
              <img src={IMG.people} alt="A family connecting together" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-people/60 to-transparent" />
              <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/90 text-people text-xs font-bold">Personal Connect · People to People</span>
            </div>
            <div className="p-7 flex flex-col flex-1">
              <div className="w-14 h-14 rounded-2xl bg-people flex items-center justify-center -mt-12 mb-4 border-4 border-white shadow-lg relative z-10">
                <Users size={28} strokeWidth={2.5} className="text-white" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-[#1A1A1A]">Connect with People</h2>
              <p className="font-bold text-people mt-1">Build your personal network.</p>
              <p className="text-[#4A4A4A] leading-relaxed mt-3 flex-1">
                Find and connect with friends, family, business contacts and people you know. Send a connection request, stay in touch and share updates — wherever you are in the world.
              </p>
              <button onClick={() => navigate("/register")} data-testid="hero-people-cta"
                className="mt-6 w-full min-h-[56px] rounded-full font-bold text-lg bg-people text-white hover:bg-people-dark transition-transform active:scale-95 flex items-center justify-center gap-2">
                Connect with People <ArrowRight size={20} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Community card */}
          <div data-testid="hero-card-connect-community" className="bg-white rounded-3xl border-2 border-community-border shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col">
            <div className="relative h-52 sm:h-60">
              <img src={IMG.community} alt="A hometown village" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-community/70 to-transparent" />
              <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-gold text-forest-900 text-xs font-bold flex items-center gap-1.5"><ShieldCheck size={14} strokeWidth={2.5} /> Community Connect · People to Their Roots</span>
            </div>
            <div className="p-7 flex flex-col flex-1">
              <div className="w-14 h-14 rounded-2xl bg-community flex items-center justify-center -mt-12 mb-4 border-4 border-white shadow-lg relative z-10">
                <Home size={28} strokeWidth={2.5} className="text-white" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-[#1A1A1A]">Connect with Your Community</h2>
              <p className="font-bold text-community mt-1">Your hometown may be far away. Your connection to it doesn't have to be.</p>
              <p className="text-[#4A4A4A] leading-relaxed mt-3 flex-1">
                Join your official hometown or village hub, receive verified updates from a trusted Town Admin, take part, support local needs, and help your roots grow — from anywhere in the world.
              </p>
              <button onClick={() => navigate("/register")} data-testid="hero-community-cta"
                className="mt-6 w-full min-h-[56px] rounded-full font-bold text-lg bg-community text-white hover:bg-community-dark transition-transform active:scale-95 flex items-center justify-center gap-2">
                Find My Community <ArrowRight size={20} strokeWidth={2.5} />
              </button>
              <p className="text-sm text-[#737373] mt-3 flex items-center gap-1.5 justify-center"><MapPin size={15} strokeWidth={2.5} className="text-terracotta" /> Starting with Begowal, Punjab — and many more towns to come.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust pillars */}
      <section className="py-12 bg-white/70 border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`pillar-${i}`}>
              <span className="w-11 h-11 rounded-xl bg-black/5 flex items-center justify-center shrink-0">
                <p.icon size={22} strokeWidth={2.5} className={p.color} />
              </span>
              <div>
                <p className="font-bold text-[#1A1A1A] leading-tight">{p.title}</p>
                <p className="text-sm text-[#737373]">{p.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators */}
      <section id="about" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta-light text-terracotta font-bold text-sm mb-5">
            <ShieldCheck size={18} strokeWidth={2.5} /> More than a social network
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">Built on real trust &amp; transparency</h2>
          <p className="text-lg text-[#4A4A4A] mt-4">We replace noisy feeds and anonymous profiles with verified people, accountable admins and open community projects.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 mt-12">
          {DIFFERENTIATORS.map((d, i) => (
            <div key={i} data-testid={`differentiator-${i}`} className="bg-white rounded-2xl border-2 border-[#E5E0D8] shadow-sm p-7 hover:border-community-border transition-colors">
              <div className="flex items-center gap-4 mb-4">
                <span className="w-14 h-14 rounded-2xl bg-community-light flex items-center justify-center shrink-0">
                  <d.icon size={28} strokeWidth={2.5} className="text-community" />
                </span>
                <div>
                  <span className="inline-block px-2.5 py-1 rounded-full bg-gold-light text-forest-900 text-xs font-bold mb-1">{d.badge}</span>
                  <h3 className="font-serif text-xl font-bold text-[#1A1A1A] leading-tight">{d.title}</h3>
                </div>
              </div>
              <p className="text-[#4A4A4A] leading-relaxed">{d.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-white/70 border-y border-[#E5E0D8] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-community-light text-community font-bold text-sm mb-5">
              <Home size={18} strokeWidth={2.5} /> Community Connect
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">How a town community works</h2>
            <p className="text-lg text-[#4A4A4A] mt-4">One trusted place where residents, former residents, families and supporters of a town come together.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`how-step-${i}`} className="bg-white rounded-2xl border-2 border-[#E5E0D8] shadow-sm p-7 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-12 rounded-2xl bg-community flex items-center justify-center shrink-0">
                    <s.icon size={24} strokeWidth={2.5} className="text-white" />
                  </span>
                  <span className="font-serif text-4xl font-bold text-community/20">{s.step}</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A1A1A] mb-2">{s.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{s.text}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {s.tags.map((t, j) => (
                    <span key={j} className="px-2.5 py-1 rounded-full bg-community-light text-community text-xs font-semibold">{t}</span>
                  ))}
                </div>
                <p className="text-sm text-[#737373] mt-4 flex items-start gap-2 pt-4 border-t border-[#E5E0D8]"><ShieldCheck size={16} strokeWidth={2.5} className="text-terracotta shrink-0 mt-0.5" /> {s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One Town. One Global Community. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">One Town. One Global Community.</h2>
        <p className="text-lg text-[#4A4A4A] mt-4 max-w-2xl mx-auto">A person may leave their hometown. Their children may grow up thousands of miles away. But the connection to their roots can continue.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            { icon: Home, t: "People living in the town" },
            { icon: MapPin, t: "People originally from the town" },
            { icon: Globe, t: "Families living around the world" },
            { icon: HandHeart, t: "People who want to contribute" },
          ].map((x, i) => (
            <div key={i} className="bg-white rounded-2xl border-2 border-[#E5E0D8] p-6 flex flex-col items-center gap-3">
              <span className="w-14 h-14 rounded-full bg-people-light flex items-center justify-center"><x.icon size={26} strokeWidth={2.5} className="text-people" /></span>
              <p className="font-semibold text-[#1A1A1A]">{x.t}</p>
            </div>
          ))}
        </div>
        <p className="font-serif text-xl sm:text-2xl font-bold text-community mt-10">Together, they become one connected community.</p>
      </section>

      {/* More than a social network — projects */}
      <section className="bg-community text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-gold font-bold text-sm mb-5"><Target size={18} strokeWidth={2.5} /> Positive community development</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight">Not just communication — real contribution</h2>
            <p className="text-lg text-white/80 mt-4">People who moved away can see where help is needed and take part in improving the place they still call home. Communities can organise and support projects like:</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
            {PROJECTS.map((p, i) => (
              <div key={i} className="bg-white/10 rounded-xl px-4 py-3 flex items-center gap-2.5">
                <p.icon size={18} strokeWidth={2.5} className="text-gold shrink-0" />
                <span className="text-sm font-medium text-white/90">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured hometown spotlight — Begowal */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-light text-forest-900 font-bold text-sm mb-5"><Globe size={18} strokeWidth={2.5} /> Model community</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">Begowal, Punjab — where it begins</h2>
          <p className="text-lg text-[#4A4A4A] mt-4 max-w-lg">A person originally from Begowal now living in Canada, the US, the UK, Australia or anywhere else can join, follow verified updates, and back local projects. We're building the full system around Begowal first — then opening it to every town.</p>
          <button onClick={() => navigate("/register")} data-testid="spotlight-join-btn" className="mt-7 min-h-[56px] px-8 rounded-full font-bold text-lg bg-community text-white hover:bg-community-dark transition-transform active:scale-95 inline-flex items-center gap-2">
            Join the Begowal community <ArrowRight size={20} strokeWidth={2.5} />
          </button>
        </div>
        <div className="bg-white rounded-3xl overflow-hidden shadow-xl border-2 border-[#E5E0D8]">
          <div className="relative h-44">
            <img src={IMG.town} alt="Begowal, Punjab" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-4 text-white">
              <p className="font-serif text-2xl font-bold flex items-center gap-2"><ShieldCheck size={20} className="text-gold" strokeWidth={2.5} /> Begowal, Punjab</p>
              <p className="text-sm text-white/80">Pioneering Town Community Hub</p>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm font-bold text-[#737373] uppercase tracking-wide mb-2">Sample project</p>
            <h3 className="font-serif text-xl font-bold text-[#1A1A1A]">Clean-water &amp; solar street lighting</h3>
            <div className="flex justify-between text-sm font-bold mt-4"><span className="text-community">$7,850 pledged</span><span className="text-[#737373]">Goal $10,000</span></div>
            <div className="h-3 rounded-full bg-black/5 overflow-hidden mt-1.5"><div className="h-full bg-terracotta rounded-full" style={{ width: "78%" }} /></div>
            <p className="text-sm text-[#737373] mt-2 flex items-center gap-1.5"><Users size={15} strokeWidth={2.5} /> 142 supporters · fully transparent ledger</p>
            <p className="text-xs text-[#9a9a9a] mt-3 italic">Illustrative example of how a Begowal project would appear.</p>
          </div>
        </div>
      </section>

      {/* For town admins */}
      <section id="for-town-admins" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20 scroll-mt-20">
        <div className="rounded-3xl bg-white border-2 border-community-border shadow-md p-8 sm:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-community-light text-community font-bold text-sm mb-5"><Award size={18} strokeWidth={2.5} /> For Town Admins</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">Bring your town to Connect</h2>
            <p className="text-lg text-[#4A4A4A] mt-4 max-w-2xl">Are you a village elder, municipal leader or trusted diaspora organiser? Apply to launch your official, verified Town Community Hub — reviewed by the Connect team, with full moderation and transparent project tools.</p>
          </div>
          <button onClick={() => navigate("/register")} data-testid="for-town-admins-apply-btn"
            className="min-h-[60px] px-8 rounded-full font-bold text-lg bg-terracotta text-white hover:bg-terracotta-dark transition-transform active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap">
            Bring Your Town to Connect <ArrowRight size={22} strokeWidth={2.5} />
          </button>
        </div>
      </section>

      {/* Choose how you want to connect */}
      <section className="bg-white/70 border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight text-center mb-3">Choose how you want to connect</h2>
          <p className="text-lg text-[#4A4A4A] text-center mb-10">Both live on one platform — switch between them any time.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border-2 border-people-border bg-people-light p-8 text-center flex flex-col items-center">
              <span className="w-16 h-16 rounded-2xl bg-people flex items-center justify-center mb-4"><Users size={30} strokeWidth={2.5} className="text-white" /></span>
              <p className="text-xs font-bold tracking-[0.2em] text-people uppercase">Personal Connect</p>
              <h3 className="font-serif text-2xl font-bold text-[#1A1A1A] mt-1">People to People</h3>
              <p className="text-[#4A4A4A] mt-2 flex-1">Build relationships with friends, family, professionals and people you know.</p>
              <button onClick={() => navigate("/register")} data-testid="choose-people-btn" className="mt-6 min-h-[52px] px-7 rounded-full font-bold bg-people text-white hover:bg-people-dark transition-transform active:scale-95 inline-flex items-center gap-2">Connect with People <ArrowRight size={18} strokeWidth={2.5} /></button>
            </div>
            <div className="rounded-3xl border-2 border-community-border bg-community-light p-8 text-center flex flex-col items-center">
              <span className="w-16 h-16 rounded-2xl bg-community flex items-center justify-center mb-4"><Home size={30} strokeWidth={2.5} className="text-white" /></span>
              <p className="text-xs font-bold tracking-[0.2em] text-community uppercase">Community Connect</p>
              <h3 className="font-serif text-2xl font-bold text-[#1A1A1A] mt-1">People to Their Roots</h3>
              <p className="text-[#4A4A4A] mt-2 flex-1">Join your hometown or village community, receive updates, participate, and help make a difference.</p>
              <button onClick={() => navigate("/register")} data-testid="choose-community-btn" className="mt-6 min-h-[52px] px-7 rounded-full font-bold bg-community text-white hover:bg-community-dark transition-transform active:scale-95 inline-flex items-center gap-2">Find My Community <ArrowRight size={18} strokeWidth={2.5} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* Closing strip */}
      <section className="py-16 bg-community text-white text-center">
        <p className="font-serif text-2xl sm:text-3xl font-bold tracking-tight px-6">Connect People. Connect Communities. Strengthen Roots.</p>
        <p className="text-white/70 mt-3 px-6 italic">No matter where you live, stay connected to where you come from.</p>
        <button onClick={() => navigate("/register")} data-testid="closing-join-btn"
          className="mt-8 min-h-[56px] px-9 rounded-full font-bold text-lg bg-terracotta text-white hover:bg-terracotta-dark transition-transform active:scale-95 inline-flex items-center gap-2">
          Join Connect free <ArrowRight size={22} strokeWidth={2.5} />
        </button>
      </section>

      <footer className="border-t border-[#E5E0D8] py-8 bg-[#FAFAF7]">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-2 text-[#737373]">
          <HeartHandshake size={20} strokeWidth={2.5} className="text-terracotta" />
          <span className="font-semibold">Connect — People · Communities · Brighter Tomorrows</span>
        </div>
      </footer>
    </div>
  );
}
